/*
 * KeyboardHandlers - Keyboard-Event-Behandlung und Dimension-Verwaltung
 *
 * Verarbeitet alle Tastatur-Interaktionen:
 * - Keyboard-Event-Routing (Dialog → Suche → Menu → Console → Editor)
 * - Pane-Navigation (Alt+Tab, Cmd+1-4, Escape)
 * - Zoom (Cmd+Plus/Minus/0)
 * - Fullscreen-Toggle
 * - Resize und Dimension-Änderungen
 *
 * Enthält auch Editor-Bounds-Berechnungen die von renderLoop benötigt werden.
 */

import { ctx } from './appContext.js'
import { COMMAND_SHORTCUTS, PaneId, PaneState, ConsoleFocus, TIMING, EDITOR } from './config.js'
import { ControlType } from './layout.js'
import { saveSettings, saveContent } from './persistence.js'
import { handleCopy, handleCut, handlePaste, writeConsoleSelection } from './clipboard.js'
import { getScrollbarPane } from './mouseHandlers.js'
import { wordRangeAt } from './spellcheck/Tokenizer.js'

/**
 * Berechnet effektive Editor-Bounds.
 * Nutzt Main-Pane-Bounds, aber bei unsichtbarem Main die Höhe des sichtbaren Panes
 * (bei Outline/Context FULLSCREEN hat Main height=0).
 */
export function effectiveEditorBounds() {
    const mainBounds = ctx.layout.panes[PaneId.MAIN].innerBounds
    if (mainBounds.height > 0) return mainBounds

    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    const contextPane = ctx.layout.panes[PaneId.CONTEXT]
    const effectiveHeight = Math.max(
        outlinePane ? outlinePane.innerBounds.height : 0,
        contextPane ? contextPane.innerBounds.height : 0
    )
    return { ...mainBounds, height: effectiveHeight }
}

/**
 * Gibt die Outline-Pane-Innenbreite zurück.
 * EditorView berechnet per-Paragraph Note-Metriken daraus.
 * @returns {number} outlinePaneWidth (0 wenn Outline nicht sichtbar)
 */
export function calcOutlinePaneWidth() {
    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    if (!outlinePane || !outlinePane.isVisible() || outlinePane.innerBounds.width <= 0) {
        return 0
    }
    // Scrollbar-Breite abziehen wenn die Scrollbar im Outline-Pane liegt
    const sbPane = getScrollbarPane(ctx.layout)
    const sbWidth = (sbPane === outlinePane) ? EDITOR.SCROLLBAR_WIDTH : 0
    return outlinePane.innerBounds.width - sbWidth
}

/**
 * Gibt die Context-Pane-Innenbreite zurück.
 * @returns {number} contextPaneWidth (0 wenn Context nicht sichtbar)
 */
export function calcContextPaneWidth() {
    const contextPane = ctx.layout.panes[PaneId.CONTEXT]
    if (!contextPane || !contextPane.isVisible() || contextPane.innerBounds.width <= 0) {
        return 0
    }
    // Scrollbar-Breite abziehen wenn die Scrollbar im Context-Pane liegt
    const sbPane = getScrollbarPane(ctx.layout)
    const sbWidth = (sbPane === contextPane) ? EDITOR.SCROLLBAR_WIDTH : 0
    return contextPane.innerBounds.width - sbWidth
}

/**
 * Prueft ob ein Paragraph im aktuellen View sichtbar ist (nicht gefiltert,
 * nicht durch Collapse versteckt, nicht geloescht).
 *
 * Exportiert fuer Tests — die Laufzeit-Logik nutzt es nur aus diesem Modul.
 */
export function _isParagraphVisibleInView(view, paragraphId) {
    if (!paragraphId) return false
    return view._paragraphLineIndex.has(paragraphId)
}

/**
 * Sucht einen sinnvollen Fallback-Paragraph fuer die Cursor-Position
 * wenn der aktuelle Cursor im neuen Pane nicht gueltig ist.
 *
 * Reihenfolge (Regeln aus Boris' Spezifikation):
 *   1. Gleicher Abschnitt wie hintParagraphId, erster sichtbarer Nicht-Separator
 *   2. Separator dieses Abschnitts (Label — immer sichtbar)
 *   3. Erster sichtbarer Paragraph im Dokument
 *
 * Exportiert fuer Tests.
 */
export function _findFallbackParagraph(model, view, hintParagraphId) {
    const paragraphs = model.paragraphs

    if (hintParagraphId) {
        const hintIdx = model.getParagraphIndex(hintParagraphId)
        if (hintIdx >= 0) {
            // Abschnittsgrenzen: letzter Separator bei/vor hintIdx (inkl. selbst),
            // naechster Separator danach (exklusiv)
            let sectionSeparator = null
            let sectionStart = 0
            for (let i = hintIdx; i >= 0; i--) {
                if (paragraphs[i].type === 'separator') {
                    sectionSeparator = paragraphs[i]
                    sectionStart = i + 1
                    break
                }
            }
            // Wenn der hintParagraph SELBST ein Separator ist, ist er der Abschnitt
            if (paragraphs[hintIdx].type === 'separator') {
                sectionSeparator = paragraphs[hintIdx]
                sectionStart = hintIdx + 1
            }

            let sectionEnd = paragraphs.length
            for (let i = sectionStart; i < paragraphs.length; i++) {
                if (paragraphs[i].type === 'separator') {
                    sectionEnd = i
                    break
                }
            }

            // 1. Erster sichtbarer Nicht-Separator im Abschnitt
            for (let i = sectionStart; i < sectionEnd; i++) {
                const p = paragraphs[i]
                if (p.type !== 'separator' && _isParagraphVisibleInView(view, p.id)) {
                    return p
                }
            }

            // 2. Separator des Abschnitts (Label)
            if (sectionSeparator && _isParagraphVisibleInView(view, sectionSeparator.id)) {
                return sectionSeparator
            }
        }
    }

    // 3. Erster sichtbarer Paragraph im Dokument (Separator zaehlt auch)
    for (const p of paragraphs) {
        if (_isParagraphVisibleInView(view, p.id)) {
            return p
        }
    }

    return null
}

/**
 * Aktiviert einen Pane und stellt sicher, dass der Editor-Cursor eine
 * gueltige, sichtbare Position in dem Pane hat.
 *
 * Zentral fuer alle Pane-Aktivierungen (Alt+Tab, Maximize, Fullscreen-Exit,
 * Control-Klicks). Loest das "Cursor verloren"-Problem beim Maximieren:
 * wird ein Pane maximiert, kann der bisherige Cursor-Paragraph durch den
 * Pane-Filter oder Collapse unsichtbar werden — dann muss der Cursor auf
 * eine sinnvolle Stelle umziehen, sonst ist das Pane "cursor-los".
 *
 * Regeln:
 *   1. Cursor-Paragraph im neuen Pane sichtbar → nur Feld wechseln (Paragraph bleibt)
 *   2. Sonst Fallback: gleicher Abschnitt → Separator → Dokumentanfang
 *
 * @param {string} paneId - PaneId.OUTLINE | MAIN | CONTEXT | CORE
 */
export function activatePane(paneId) {
    if (!ctx.layout.panes[paneId] || !ctx.layout.panes[paneId].isVisible()) return

    // Layout-Seite setzen
    ctx.layout.setActivePane(paneId)

    // Core hat eigenen Cursor — Editor-Cursor-Logik ueberspringen
    if (paneId === PaneId.CORE || !ctx.editor) return

    // Editor-Bounds mit aktuellem Layout synchron halten: handleControlClick
    // (Maximize/Collapse) hat die Pane-Bounds schon geaendert, aber
    // syncEditorSize() laeuft erst im naechsten renderLoop. Ohne dieses
    // Pre-Sync validiert die Cursor-Pruefung unten gegen ein _paragraphLineIndex
    // vom Vor-Maximize-Zustand — der Filter ist da noch nicht aktiv, jeder
    // Paragraph gilt als sichtbar, und der Cursor landet auf einem Absatz
    // der im naechsten Frame aus den Lines rausgefiltert wird.
    const newBounds = effectiveEditorBounds()
    const newOutlineWidth = calcOutlinePaneWidth()
    const newContextWidth = calcContextPaneWidth()
    if (ctx.editor.bounds.width !== newBounds.width ||
        ctx.editor.bounds.height !== newBounds.height ||
        ctx.editor.bounds.x !== newBounds.x ||
        ctx.editor.bounds.y !== newBounds.y ||
        ctx.editor.view.outlinePaneWidth !== newOutlineWidth ||
        ctx.editor.view.contextPaneWidth !== newContextWidth) {
        ctx.editor.resize(newBounds, newOutlineWidth, newContextWidth)
        ctx.editor.view.markDirty()
        ctx.layout.isDirty = true
        ctx.layout.borderDirty = true
    }

    const targetField = paneId === PaneId.OUTLINE ? 'note'
        : paneId === PaneId.CONTEXT ? 'info'
        : 'text'

    const model = ctx.editor.model
    const view = ctx.editor.view
    const cursor = model.cursor

    // rebuildIfDirty sicherstellen — falls STRUCTURAL/PARAGRAPH dirty
    view.rebuildIfDirty()

    const currentValid = cursor && _isParagraphVisibleInView(view, cursor.paragraphId)

    if (currentValid) {
        // Separator hat keine renderbare Position im note/info-Feld (alle
        // entsprechenden Lines sind Padding, _getFieldLineIndexForPosition
        // liefert -1, Context/Outline-Renderer bekommt keine Cursor-Koordinaten).
        // Separator-Titel lebt immer im text-Feld und ist in allen Panes als
        // Section-Header sichtbar (konsistent mit handleInfoClick).
        const effectiveField = model.isSeparator(cursor.paragraphId) ? 'text' : targetField
        if (cursor.field !== effectiveField) {
            if (effectiveField === 'note') {
                ctx.editor.switchToNote()
            } else {
                ctx.editor.switchField(effectiveField)
            }
        }
        view.ensureCursorVisible()
        return
    }

    // Cursor ungueltig oder nicht vorhanden — Fallback suchen
    const fallbackPara = _findFallbackParagraph(model, view, cursor ? cursor.paragraphId : null)
    if (fallbackPara) {
        // Separator-Fallback immer im text-Feld (Titel-Bearbeitung) — sonst
        // waere der Cursor im info/note-Feld eines Separators unrenderbar.
        const effectiveField = fallbackPara.type === 'separator' ? 'text' : targetField
        model.setCursor(fallbackPara.id, 0, effectiveField)
        view.ensureCursorVisible()
    }
}

/**
 * Behandelt Window-Resize.
 */
export function handleResize() {
    const dims = ctx.renderer.handleResize()
    handleDimensionChange(dims)
}

/**
 * Behandelt Dimension-Änderungen (Resize, Zoom, Font-Wechsel).
 *
 * @param {{cols: number, rows: number}} dims - Neue Dimensionen
 */
export function handleDimensionChange(dims) {
    ctx.screen.resize(dims.cols, dims.rows)
    ctx.cursor.updateBounds(dims.cols, dims.rows)
    ctx.overlay.resize(dims.cols, dims.rows)
    if (ctx.overlayTop) ctx.overlayTop.resize(dims.cols, dims.rows)
    ctx.layout.resize(dims.cols, dims.rows)

    // Editor resizen
    if (ctx.editor) {
        const mainPane = ctx.layout.panes[PaneId.MAIN]
        if (mainPane) {
            ctx.editor.resize(effectiveEditorBounds(), calcOutlinePaneWidth(), calcContextPaneWidth())
        }
    }

    // Console-Layout invalidieren (Font/Zoom ändert Metriken-Cache)
    if (ctx.coreConsole) {
        ctx.coreConsole.model.invalidateLayout()
    }

    // Context Menu bei Resize schließen
    if (ctx.contextMenu && ctx.contextMenu.isOpen) {
        ctx.contextMenu.close()
    }

    // Spellcheck-Menu bei Resize schliessen — Anchor passt sonst nicht mehr
    if (ctx.spellCheckMenu && ctx.spellCheckMenu.isOpen) {
        ctx.spellCheckMenu.close()
    }

    // Menu neu rendern wenn offen
    if (ctx.menu.isMenuOpen()) {
        ctx.menu.close()
        ctx.menu.open()
    }

    // Project Dashboard live mitresizen — sonst bleibt der Frame an der
    // alten Position und Pane-Border-Reste werden erst beim Drag-Ende neu
    // gemalt. handleResize() macht beides (Re-Layout + Border-Refresh).
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) {
        ctx.projectDashboard.handleResize()
    }

    // Phase 6.0c: nach jedem Re-Layout den Cursor des aktuell tippbaren
    // Editors in den (evtl. geschrumpften) Viewport scrollen. EditorView.resize
    // haelt zwar den mittleren sichtbaren Anker stabil — der Cursor kann aber
    // beim Eintippen unten gestanden haben und durch eine einblendende
    // Soft-Keyboard hinter den Sichtbarkeits-Rand rutschen. ensureCursorVisible
    // ist idempotent, wenn der Cursor ohnehin sichtbar ist.
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor && ctx.memoryEditor.proseEditor.view) {
        ctx.memoryEditor.proseEditor.view.ensureCursorVisible()
    } else if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor && ctx.personalDictEditor.proseEditor.view) {
        ctx.personalDictEditor.proseEditor.view.ensureCursorVisible()
    } else if (ctx.layout.consoleFocus === ConsoleFocus.FOCUSED
        && ctx.coreConsole && ctx.coreConsole.view) {
        ctx.coreConsole.view.ensureCursorVisible()
    } else if (ctx.editor && ctx.editor.view) {
        ctx.editor.view.ensureCursorVisible()
    }
}

/**
 * Entscheidet, ob ein Keydown den Mauscursor verstecken soll.
 *
 * Nur "echte" Tipp-Tasten verstecken — keine reinen Modifier (Shift allein),
 * Function-Keys, Navigation oder Escape. Sonst wuerde der Cursor bei jedem
 * Tastendruck verschwinden, obwohl gar nicht "getippt" wird (z.B. Cmd+Tab,
 * Pfeiltasten, F-Tasten). Reduziert ausserdem die Haeufigkeit eines bekannten
 * Browser-Quirks, bei dem das Re-Show via `cursor: none` -> `auto` einen
 * Mausevent zu spaet sichtbar wird — der Cursor bleibt dann unsichtbar bis
 * man den Canvas verlaesst und wieder eintritt.
 *
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
export function isTypingKey(e) {
    // Druckbare Zeichen: Buchstaben, Zahlen, Symbole, Space (key.length === 1)
    if (e.key && e.key.length === 1) return true
    // Text-Editing + IME-Composition + Dead-Keys (Compose fuer Umlaute / Backtick)
    if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter'
        || e.key === 'Tab' || e.key === 'Dead' || e.key === 'Process') {
        return true
    }
    return false
}

/**
 * Behandelt Keyboard-Events.
 *
 * @param {KeyboardEvent} e
 */
export function handleKeydown(e) {
    // Mauscursor bei echter Tastatureingabe ausblenden (nicht bei reinen
    // Modifier-Tasten, F-Tasten, Pfeiltasten, Escape) — siehe isTypingKey().
    if (isTypingKey(e) && ctx.renderer.canvas.style.cursor !== 'none') {
        ctx.renderer.canvas.style.cursor = 'none'
    }

    // Doppel-Ctrl-Tracking fuer Diktat
    if (ctx.dictationManager) {
        if (e.key === 'Control') {
            ctx.dictationManager.handleCtrlDown()
        } else if (e.ctrlKey) {
            // Ctrl+irgendwas → kein alleiniges Ctrl
            ctx.dictationManager.handleCtrlCombination()
        }

        // Bei aktivem Diktat: Interim akzeptieren wenn User tippt (nicht Ctrl/Shift/Alt/Meta)
        if (ctx.dictationManager.hasOpenInterim && !ctx.dictationManager.isActive &&
            !e.ctrlKey && !e.metaKey && !e.altKey &&
            e.key !== 'Control' && e.key !== 'Shift' && e.key !== 'Alt' && e.key !== 'Meta') {
            if (e.key !== 'Backspace') {
                // Jede Taste außer Backspace akzeptiert den Interim-Text
                ctx.dictationManager.acceptInterim()
            }
        }
    }

    // UserMenu hat Priorität wenn offen. Alle Tasten werden geblockt — analog
    // zum Hauptmenue. Ohne preventDefault wuerden z.B. Cmd+/-/0 an den Browser
    // durchgereicht und den Browser-Zoom triggern, statt vom Menue konsumiert
    // zu werden.
    if (ctx.userMenu && ctx.userMenu.isOpen) {
        e.preventDefault()
        ctx.userMenu.handleKeydown(e, ctx.user, ctx.layout.cols)
        ctx.screen.markDirty()
        return
    }

    // Memory-Editor: wenn offen, fängt der Wrapper problematische Shortcuts
    // ab; alles andere geht an den eigenen ProseEditor des Memory-Editors —
    // der Haupt-Editor (ctx.editor) bleibt unberuehrt.
    //
    // Ausnahme: Cmd+Shift+D fuer das Project-Dashboard wird durchgelassen
    // (Spec §2 close()): Dashboard ist modales Overlay, ESC schliesst es,
    // Memory-Editor bleibt offen.
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()) {
        // ESC schliesst den Memory-Editor — analog zum Klick auf das (-)-
        // Control im Pane-Top-Border (mouseHandlers.js Z 767-773) und
        // konsistent mit ESC im Hauptpfad (Pane-Fullscreen-Toggle = was
        // das (-)-Control im jeweiligen Pane macht). Async via IIFE,
        // weil close() einen Save flusht + Lock freigibt.
        if (e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
            e.preventDefault()
            ;(async () => {
                try { await ctx.memoryEditor.close() }
                catch (err) { console.warn('Memory-Editor close on ESC failed:', err.message) }
                ctx.layout.isDirty = true
            })()
            return
        }
        const cmdKey = e.ctrlKey || e.metaKey
        const isDashboardShortcut = cmdKey && e.shiftKey && !e.altKey
            && (e.key === 'D' || e.key === 'd')
        if (!isDashboardShortcut) {
            // Zoom-Shortcuts (Cmd+/-/0) zuerst — sonst schlaegt Cmd+/-
            // auf den Browser-Zoom durch (Editor-Pfade returnen frueh,
            // bevor handleZoomShortcut am Funktionsende erreicht waere).
            if (handleZoomShortcut(e)) return
            // Cmd+D: aktuellen Absatz loeschen — analog Hauptpfad, aber
            // ohne Section-Lock/Subtree-Logik (beide Overlay-Editoren haben
            // keine Sections). Sonst feuert Browser-Default (Bookmark-Dialog).
            if (_handleOverlayCmdD(e, ctx.memoryEditor.proseEditor)) return
            // Cmd+C / Cmd+X / Cmd+V: Clipboard-Helper (gehen ueber
            // navigator.clipboard, brauchen async Wrapping)
            if (cmdKey && !e.altKey && !e.shiftKey) {
                if (e.key === 'c' || e.key === 'C') {
                    e.preventDefault()
                    handleCopy()
                    return
                }
                if (e.key === 'x' || e.key === 'X') {
                    e.preventDefault()
                    handleCut()
                    return
                }
                if (e.key === 'v' || e.key === 'V') {
                    e.preventDefault()
                    handlePaste()
                    return
                }
            }
            if (ctx.memoryEditor.handleKeydown(e)) {
                ctx.layout.isDirty = true
                return
            }
            if (ctx.memoryEditor.proseEditor
                && ctx.memoryEditor.proseEditor.handleKeydown(e)
            ) {
                // Browser-Default unterdruecken (Tab → Fokus-Wechsel,
                // Backspace → History-Back, etc.) — analog zum Haupt-Editor
                e.preventDefault()
                ctx.layout.isDirty = true
                return
            }
            // Catch-All: nicht behandeltes Tab darf den Canvas-Fokus NICHT
            // verlieren (analog zum Hauptpfad in dieser Datei). Andere Tasten
            // ohne Editor-Wirkung lassen wir durch (z.B. Cmd+R = Reload).
            if (e.key === 'Tab') e.preventDefault()
            return
        }
        // Cmd+Shift+D: Fall-through zum Dashboard-Shortcut weiter unten
    }

    // Personal-Dict-Editor: strukturgleich zu Memory-Editor. Eigene
    // Wrapper-Filter blocken Format-Shortcuts und Block-Strukturierung
    // (Bold/Italic/Headings/Lists), die in einer Plain-Text-Wortliste
    // keinen Sinn ergeben (Spellcheck §10 Phase 4).
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()) {
        // ESC schliesst den Personal-Dict-Editor — analog Memory-Editor,
        // analog zum Klick auf das (-)-Control (mouseHandlers.js Z 776-783).
        // close() flusht den finalen Save (Save-on-close-Pattern) und kann
        // im Cap-Fall false zurueckgeben — der Editor bleibt dann offen.
        if (e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
            e.preventDefault()
            ;(async () => {
                try { await ctx.personalDictEditor.close() }
                catch (err) { console.warn('Personal-Dict close on ESC failed:', err.message) }
                ctx.layout.isDirty = true
            })()
            return
        }
        const cmdKey = e.ctrlKey || e.metaKey
        const isDashboardShortcut = cmdKey && e.shiftKey && !e.altKey
            && (e.key === 'D' || e.key === 'd')
        if (!isDashboardShortcut) {
            if (handleZoomShortcut(e)) return
            if (_handleOverlayCmdD(e, ctx.personalDictEditor.proseEditor)) return
            if (cmdKey && !e.altKey && !e.shiftKey) {
                if (e.key === 'c' || e.key === 'C') {
                    e.preventDefault(); handleCopy(); return
                }
                if (e.key === 'x' || e.key === 'X') {
                    e.preventDefault(); handleCut(); return
                }
                if (e.key === 'v' || e.key === 'V') {
                    e.preventDefault(); handlePaste(); return
                }
            }
            if (ctx.personalDictEditor.handleKeydown(e)) {
                ctx.layout.isDirty = true
                return
            }
            if (ctx.personalDictEditor.proseEditor
                && ctx.personalDictEditor.proseEditor.handleKeydown(e)
            ) {
                e.preventDefault()
                ctx.layout.isDirty = true
                return
            }
            if (e.key === 'Tab') e.preventDefault()
            return
        }
    }

    // Dashboard hat Priorität wenn offen
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) {
        ctx.projectDashboard.handleKeydown(e)
        ctx.screen.markDirty()
        return
    }

    // Dialog hat höchste Priorität wenn offen
    if (ctx.dialogManager && ctx.dialogManager.isDialogOpen()) {
        if (ctx.dialogManager.handleKeydown(e)) {
            e.preventDefault()
            ctx.overlay.markDirty()
            return
        }
    }

    // Suche hat Priorität wenn offen (nach Dialog, vor Menu)
    if (ctx.searchManager && ctx.searchManager.isSearchOpen()) {
        if (ctx.searchManager.handleKeydown(e)) {
            e.preventDefault()

            if (ctx.searchManager.isSearchOpen()) {
                // Suche noch offen (Enter/Shift+Enter): Cursor zum aktuellen Match bewegen
                const matchPos = ctx.searchManager.getCurrentMatchPosition()
                if (matchPos && ctx.editor) {
                    ctx.editor.model.setCursor(matchPos.paragraphId, matchPos.offset, matchPos.field)

                    // Separator-Match: Eingabe aktivieren und Cursor auf Match-Offset setzen
                    if (ctx.editor.model.isSeparator(matchPos.paragraphId)) {
                        ctx.editor.input._activateSeparatorInput(matchPos.offset)
                    }

                    ctx.editor.view.ensureCursorVisible(2)

                    if (matchPos.field === 'note') {
                        ctx.layout.setActivePane(PaneId.OUTLINE)
                    } else if (matchPos.field === 'info') {
                        ctx.layout.setActivePane(PaneId.CONTEXT)
                    } else {
                        ctx.layout.setActivePane(PaneId.MAIN)
                    }
                }
            } else if (ctx.editor) {
                // Suche wurde geschlossen (ESC): Pane anhand des Cursor-Feldes wechseln
                const field = ctx.editor.model.cursor?.field || 'text'
                if (field === 'note') {
                    ctx.layout.setActivePane(PaneId.OUTLINE)
                } else if (field === 'info') {
                    ctx.layout.setActivePane(PaneId.CONTEXT)
                } else {
                    ctx.layout.setActivePane(PaneId.MAIN)
                }
                // currentStation auf Cursor des neuen Panes setzen
                // (sonst zeigt renderCursorLayer keinen Cursor, und Tab startet falsch)
                ctx.layout.exitNavigationMode()

                // Navigation-History: Sprung von Cmd+F-Position zur Escape-Position registrieren
                if (ctx._searchHistoryFromPos) {
                    const toPos = ctx.editor.input._getCurrentHistoryPos()
                    if (toPos && (toPos.paragraphId !== ctx._searchHistoryFromPos.paragraphId
                        || toPos.offset !== ctx._searchHistoryFromPos.offset
                        || toPos.field !== ctx._searchHistoryFromPos.field)) {
                        ctx.editor.input.navigationHistory.navigate(ctx._searchHistoryFromPos, toPos)
                    }
                    ctx._searchHistoryFromPos = null
                }
            }

            ctx.layout.isDirty = true
            return
        }
    }

    // Spellcheck-Menu: ↑↓ Enter Esc selbst behandeln, alle anderen Tasten
    // schliessen es (analog ContextMenu).
    if (ctx.spellCheckMenu && ctx.spellCheckMenu.isOpen) {
        if (ctx.spellCheckMenu.handleKeydown(e)) {
            ctx.screen.markDirty()
            ctx.layout.borderDirty = true
            ctx.layout.isDirty = true
            return
        }
        ctx.spellCheckMenu.close()
        ctx.screen.markDirty()
        ctx.layout.borderDirty = true
        // Andere Tasten: Menue geschlossen, Taste wird normal weiterverarbeitet
    }

    // Context Menu: jede Taste schließt es, Escape wird konsumiert, andere durchgereicht
    if (ctx.contextMenu && ctx.contextMenu.isOpen) {
        ctx.contextMenu.close()
        ctx.screen.markDirty()
        // Menü kann Border-Zellen überdecken — Border neu zeichnen lassen,
        // sonst bleiben Pixel-Reste auf dem Pane-Rahmen stehen.
        ctx.layout.borderDirty = true
        if (e.key === 'Escape') {
            e.preventDefault()
            return
        }
        // Andere Tasten: Menü geschlossen, Taste wird normal weiterverarbeitet
    }

    // Menü hat Priorität wenn offen
    if (ctx.menu.isMenuOpen()) {
        if (ctx.menu.handleKeydown(e)) {
            e.preventDefault()
            ctx.screen.markDirty()
            return
        }
    }

    // Command-Palette Shortcuts prüfen
    if (isCommandPaletteShortcut(e)) {
        e.preventDefault()
        ctx.menu.open()
        ctx.screen.markDirty()
        return
    }

    // Editor ist immer verfügbar (unabhängig von Console-Fokus)
    const editorPaneId = ctx.layout.getActiveEditorPane()
    const isEditorPane = ctx.editor &&
        (editorPaneId === PaneId.MAIN || editorPaneId === PaneId.OUTLINE || editorPaneId === PaneId.CONTEXT)

    // Ctrl+Shift+D: Projekt-Dashboard öffnen/schließen
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.key === 'D') {
        e.preventDefault()
        if (ctx.projectDashboard) {
            if (ctx.projectDashboard.isOpen) {
                ctx.projectDashboard.close()
            } else {
                ctx.projectDashboard.open()
            }
            ctx.screen.markDirty()
        }
        return
    }

    // Console-Fokus aktiv? (HOVER oder FOCUSED — Tastatur geht an Console)
    const consoleActive = ctx.coreConsole && ctx.layout.isConsoleActive() && !ctx.layoutNavigationMode

    // Delta-Review-Modus: ReviewController greift A/R/T/←/→/Escape ab, solange
    // ein Review laeuft — aber nur wenn der User im Main- oder Core-Pane
    // arbeitet. Outline/Context/Background bleiben voll bedienbar (Spec §7:
    // "Sperre nur fuer den Main-Pane"). So darf der User waehrend der Review
    // in der Outline navigieren oder Notizen am Paragraph machen, waehrend der
    // Review im Main wartet.
    if (ctx.reviewController?.isActive) {
        const inLockedPane = ctx.layout.activePane === PaneId.MAIN || consoleActive
        if (inLockedPane && ctx.reviewController.handleKey(e)) {
            e.preventDefault()
            ctx.layout.isDirty = true
            ctx.screen.markDirty()
            return
        }
    }

    // Ctrl+C / Ctrl+`: Console-Fokus-Toggle
    if (e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey &&
        (e.key === 'c' || e.code === 'Backquote')) {
        // Ctrl+C nur auf macOS als Toggle (auf anderen Plattformen ist Ctrl+C = Copy)
        const isMac = navigator.platform.indexOf('Mac') !== -1 ||
            navigator.userAgent.indexOf('Mac') !== -1
        if (e.code === 'Backquote' || (e.key === 'c' && isMac)) {
            e.preventDefault()
            if (ctx.layout.isConsoleActive()) {
                // Console deaktivieren, ggf. Fullscreen beenden
                const corePane = ctx.layout.panes[PaneId.CORE]
                if (corePane && corePane.state === PaneState.FULLSCREEN) {
                    ctx.layout.handleControlClick({ type: ControlType.COLLAPSE, paneId: PaneId.CORE })
                }
                ctx.layout.setConsoleFocus(ConsoleFocus.NONE)
            } else {
                // Console fokussieren + Scroll-to-Input
                ctx.layout.setConsoleFocus(ConsoleFocus.FOCUSED)
                if (ctx.coreConsole) ctx.coreConsole.view.scrollToBottom()
            }
            saveSettings()
            return
        }
    }

    // Cmd+F / Ctrl+F: Suche öffnen (immer auf Editor, auch bei Console-Fokus)
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey &&
        (e.key === 'f' || e.key === 'F') && isEditorPane) {
        e.preventDefault()
        const cols = ctx.screen.cols
        const cursorPos = ctx.editor.getCursorPosition()
        // Startposition fuer Navigation-History merken
        ctx._searchHistoryFromPos = ctx.editor.input._getCurrentHistoryPos()
        ctx.searchManager.open(cols, cursorPos)
        ctx.layout.isDirty = true
        return
    }

    // Cmd+. : Spellcheck-Menue oeffnen, wenn der Cursor auf einem Wort
    // mit Warning steht. Sonst still nichts (Spec §7.3.4).
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key === '.' && isEditorPane) {
        e.preventDefault()
        _openSpellCheckMenuAtCursor()
        ctx.screen.markDirty()
        return
    }

    // Cmd+D: Aktuellen Absatz löschen
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey &&
        (e.key === 'd' || e.key === 'D') && isEditorPane) {
        e.preventDefault()
        const model = ctx.editor.model
        // Section Lock: Absatz-Löschung blockieren
        if (ctx.editor.input._isLockedAtCursor()) {
            ctx.editor.input._pingBlocked()
        } else if (model.hasSelection()) {
            // Bei Selektion: blockiert
            ctx.editor.input._pingBlocked()
        } else if (model.cursor) {
            // Collapsed Host → gesamten Subtree löschen
            const para = model.getParagraph(model.cursor.paragraphId)
            const idx = model.getParagraphIndex(model.cursor.paragraphId)
            if (para && idx >= 0 && para.type === 'separator' && para.sectionCollapsed) {
                // Section-Subtree: Separator + alles bis zum nächsten Sep mit depth ≤ self
                let endIdx = model.paragraphs.length - 1
                for (let i = idx + 1; i < model.paragraphs.length; i++) {
                    const p = model.paragraphs[i]
                    if (p.type === 'separator' && p.outlineDepth <= para.outlineDepth) {
                        endIdx = i - 1
                        break
                    }
                }
                model.deleteOutlineSubtree(idx, endIdx)
            } else if (para && idx >= 0 && para.isOutlineItem() && para.activeCollapsed) {
                // Active-Subtree (outline)
                const range = model.getSubtreeRange(idx)
                model.deleteOutlineSubtree(range.startIndex, range.endIndex)
            } else if ((model.cursor.field || 'text') === 'info') {
                // Info-Feld: nur den Info-Text leeren, Absatz NICHT loeschen
                // (gefuehlt konsistenter). Cursor zurueck in den Main-Text auf
                // Position 0 — der gemeinsame Code unten aktiviert Main.
                const infoParaId = model.cursor.paragraphId
                model.clearField(infoParaId, 'info')
                model.setCursor(infoParaId, 0, 'text')
            } else {
                model.deleteParagraph(model.cursor.paragraphId)
            }
            ctx.editor.switchField('text')
            ctx.layout.setActivePane(PaneId.MAIN)
            ctx.layout.exitNavigationMode()
            ctx.layoutNavigationMode = false
            ctx.editor.view.ensureCursorVisible(2)
        }
        ctx.layout.isDirty = true
        return
    }

    // Cmd+1/2/3/4: Direkt zu Pane springen
    const cmdKey = e.metaKey || e.ctrlKey
    if (cmdKey && !e.altKey && !e.shiftKey && ['1','2','3','4'].includes(e.key)) {
        e.preventDefault()
        const paneMap = { '1': PaneId.OUTLINE, '2': PaneId.MAIN, '3': PaneId.CONTEXT, '4': PaneId.CORE }
        const target = paneMap[e.key]
        if (ctx.layout.panes[target]?.state !== 'COLLAPSED') {
            if (target === PaneId.CORE) {
                // Cmd+4: Console fokussieren (Editor-Pane bleibt aktiv)
                ctx.layout.setConsoleFocus(ConsoleFocus.FOCUSED)
                if (ctx.coreConsole) ctx.coreConsole.view.scrollToBottom()
            } else {
                // Cmd+1/2/3: Editor-Pane wechseln, Console deaktivieren
                ctx.layout.setConsoleFocus(ConsoleFocus.NONE)
                ctx.layoutNavigationMode = false
                activatePane(target)
            }
            saveSettings()
        }
        return
    }

    // Cmd+L: Layout-Modus togglen
    if (cmdKey && !e.altKey && !e.shiftKey && (e.key === 'l' || e.key === 'L')) {
        e.preventDefault()
        if (ctx.layoutNavigationMode) {
            ctx.layoutNavigationMode = false
            ctx.layout.exitNavigationMode()
        } else {
            ctx.layoutNavigationMode = true
            ctx.layout.enterNavigationMode()
        }
        return
    }

    // Alt+Plus / Alt+Minus: Pane-Controls (+/-) des aktiven Panes auslösen
    // e.code variiert je nach Keyboard-Layout (DE: BracketRight/Slash, US: Equal/Minus, Numpad)
    if (e.altKey && !e.metaKey && !e.ctrlKey) {
        const isPlus = e.code === 'BracketRight' || e.code === 'Equal' || e.code === 'NumpadAdd'
        const isMinus = e.code === 'Slash' || e.code === 'Minus' || e.code === 'NumpadSubtract'
        if (isPlus || isMinus) {
            e.preventDefault()
            const type = isPlus ? ControlType.EXPAND : ControlType.COLLAPSE
            ctx.layout.handleControlClick({ type, paneId: ctx.layout.activePane })
            // Cursor-Position im (evtl. neu aktivierten) Pane validieren
            activatePane(ctx.layout.activePane)
            saveSettings()
            return
        }
    }

    // Cmd+= / Cmd+#: Separator oberhalb des aktuellen Absatzes einfügen
    // DE-Keyboard: = ist Shift+0 (code Digit0), # ist eigene Taste (code Backslash)
    // US-Keyboard: = ist code Equal
    if (cmdKey && !e.altKey && (
        e.code === 'Backslash' ||
        (e.code === 'Digit0' && e.shiftKey) ||
        (e.code === 'Equal' && !e.shiftKey)
    )) {
        e.preventDefault()
        ctx.layout.handleControlClick({ type: ControlType.SEPARATOR, paneId: PaneId.OUTLINE })
        ctx.layout.isDirty = true
        return
    }

    // Console hat Priorität wenn HOVER oder FOCUSED (Tastatur geht an Console)
    if (consoleActive) {
        // Bei HOVER → automatisch auf FOCUSED wechseln (User hat getippt)
        if (ctx.layout.consoleFocus === ConsoleFocus.HOVER) {
            ctx.layout.setConsoleFocus(ConsoleFocus.FOCUSED)
        }
        // Clipboard in Core Console (mit Format-Healing)
        if (cmdKey && !e.altKey && !e.shiftKey) {
            if (e.key === 'v' || e.key === 'V') {
                e.preventDefault()
                // HTML + Plain-Text lesen (analog Editor-Paste in clipboard.js).
                // Format aus HTML hat Vorrang, Plaintext-Markdown ist Fallback.
                ;(async () => {
                    let html = null
                    let text = null
                    try {
                        if (navigator.clipboard.read) {
                            try {
                                const items = await navigator.clipboard.read()
                                for (const item of items) {
                                    if (item.types.includes('text/html') && !html) {
                                        const blob = await item.getType('text/html')
                                        html = await blob.text()
                                    }
                                    if (item.types.includes('text/plain') && !text) {
                                        const blob = await item.getType('text/plain')
                                        text = await blob.text()
                                    }
                                }
                            } catch {
                                text = await navigator.clipboard.readText()
                            }
                        } else {
                            text = await navigator.clipboard.readText()
                        }
                        if (text || html) {
                            ctx.coreConsole.input.handlePaste(text || '', html)
                            ctx.layout.isDirty = true
                        }
                    } catch (err) {
                        console.warn('Console paste failed:', err)
                    }
                })()
                return
            }
            if (e.key === 'c' || e.key === 'C') {
                e.preventDefault()
                const text = ctx.coreConsole.input.handleCopy()
                // Console-Inhalt ist Markdown → zusätzlich formatiertes HTML schreiben,
                // sonst landen Marker (**fett**, # Überschrift) wörtlich in Drittapps.
                if (text) writeConsoleSelection(text)
                return
            }
            if (e.key === 'x' || e.key === 'X') {
                e.preventDefault()
                const text = ctx.coreConsole.input.handleCut()
                if (text) writeConsoleSelection(text)
                ctx.layout.isDirty = true
                return
            }
        }

        if (!(e.key === 'Enter' && ctx.layout.isOnControl())) {
            if (ctx.coreConsole.handleKeydown(e)) {
                e.preventDefault()
                ctx.layout.isDirty = true
                return
            }
        }
    }

    // Editor hat Priorität wenn Console nicht aktiv und nicht im Layout-Navigations-Modus
    if (isEditorPane && !consoleActive && !ctx.layoutNavigationMode) {
        // Clipboard-Shortcuts (Cmd+C, Cmd+X, Cmd+V)
        if (cmdKey && !e.altKey && !e.shiftKey) {
            if (e.key === 'c' || e.key === 'C') {
                e.preventDefault()
                handleCopy()
                return
            }
            if (e.key === 'x' || e.key === 'X') {
                e.preventDefault()
                handleCut()
                return
            }
            if (e.key === 'v' || e.key === 'V') {
                e.preventDefault()
                handlePaste()
                return
            }
        }

        // Tab geht zuerst an Editor (Listen-Indent/Outline), Enter nur wenn nicht auf Control
        if (!(e.key === 'Enter' && ctx.layout.isOnControl())) {
            if (ctx.editor.handleKeydown(e)) {
                e.preventDefault()
                ctx.layout.isDirty = true
                return
            }
        }
    }

    // Escape: Pane-Fullscreen-Toggle (wenn kein Overlay/Picker/Stream Escape konsumiert hat)
    if (e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        // Console aktiv (HOVER oder FOCUSED): Core-Pane togglen + fokussieren
        if (ctx.layout.isConsoleActive()) {
            const corePane = ctx.layout.panes[PaneId.CORE]
            if (corePane) {
                const type = corePane.state === PaneState.FULLSCREEN
                    ? ControlType.COLLAPSE : ControlType.EXPAND
                ctx.layout.handleControlClick({ type, paneId: PaneId.CORE })
                ctx.layout.setConsoleFocus(ConsoleFocus.FOCUSED)
                saveSettings()
            }
        } else {
            // Editor-Pane Fullscreen-Toggle
            const pane = ctx.layout.panes[ctx.layout.getActiveEditorPane()]
            if (pane) {
                const type = pane.state === PaneState.FULLSCREEN
                    ? ControlType.COLLAPSE : ControlType.EXPAND
                ctx.layout.handleControlClick({ type, paneId: ctx.layout.getActiveEditorPane() })
                // Cursor-Position im (evtl. neu aktivierten) Pane validieren
                activatePane(ctx.layout.activePane)
                saveSettings()
            }
        }
        return
    }

    // Alt+Tab: Pane-Navigation (ersetzt bisheriges Tab)
    if (e.key === 'Tab' && e.altKey) {
        e.preventDefault()
        const now = Date.now()
        const timeSinceLastTab = now - ctx.lastTabTime
        ctx.lastTabTime = now

        // Doppel-Alt+Tab erkannt?
        if (timeSinceLastTab < TIMING.doubleTabThreshold) {
            if (ctx.layoutNavigationMode) {
                // Im Layout-Modus: Doppel-Tab → verlasse Layout-Modus
                ctx.layoutNavigationMode = false
                ctx.layout.exitNavigationMode()
                const currentPaneState = ctx.layout.panes[ctx.layout.activePane]?.state
                if (currentPaneState === 'COLLAPSED') {
                    if (e.shiftKey) {
                        ctx.layout.navigateToPreviousPane()
                    } else {
                        ctx.layout.navigateToNextPane()
                    }
                }
                saveSettings()
            } else {
                // Im Pane-Modus: Doppel-Tab → aktiviere Layout-Modus
                ctx.layoutNavigationMode = true
                if (ctx.previousActivePane !== null) {
                    ctx.layout.setActivePane(ctx.previousActivePane)
                }
                ctx.layout.enterNavigationMode()
            }
            return
        }

        // Im Layout-Modus: nur durch Controls navigieren
        if (ctx.layoutNavigationMode) {
            if (e.shiftKey) {
                ctx.layout.navigateToPreviousControl()
            } else {
                ctx.layout.navigateToNextControl()
            }
            return
        }

        // Normaler Bereichswechsel (immer Editor-Pane merken, nie Core)
        ctx.previousActivePane = ctx.layout.getActiveEditorPane()
        if (e.shiftKey) {
            ctx.layout.navigateToPreviousPane()
        } else {
            ctx.layout.navigateToNextPane()
        }

        // Feld-Wechsel + Cursor-Validierung (Fallback wenn aktueller Absatz
        // im neuen Pane nicht sichtbar ist, z.B. durch Collapse oder Filter)
        activatePane(ctx.layout.activePane)

        saveSettings()
        return
    }

    // Nacktes Tab das Editor nicht behandelt hat → nichts tun (Browser-Default verhindern)
    if (e.key === 'Tab') {
        e.preventDefault()
        return
    }

    // Enter: Aktiviert Control wenn auf einem, sonst nichts (später: Zeilenumbruch)
    if (e.key === 'Enter' && ctx.layout.isOnControl()) {
        e.preventDefault()
        ctx.layout.activateCurrentStation()
        saveSettings()

        // Layout-Modus verlassen wenn jetzt auf Cursor
        if (!ctx.layout.isOnControl()) {
            ctx.layoutNavigationMode = false
        }
        return
    }

    // Zoom Shortcuts (direkt, ohne Menü)
    if (handleZoomShortcut(e)) {
        return
    }
}

/**
 * Keyup-Handler fuer Doppel-Ctrl-Erkennung (Diktat).
 * Ctrl allein gedrueckt und losgelassen → DictationManager verarbeiten.
 */
export function handleKeyup(e) {
    if (e.key !== 'Control') return
    if (!ctx.dictationManager) return

    // Dialog/Suche/Menue offen → kein Diktat-Toggle
    if (ctx.dialogManager?.isDialogOpen()) return
    if (ctx.searchManager?.isSearchOpen()) return
    if (ctx.menu?.isMenuOpen()) return

    if (ctx.dictationManager.handleCtrlUp()) {
        e.preventDefault()
    }
}

/**
 * Prüft ob ein Tastendruck ein Command-Palette Shortcut ist.
 *
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
function isCommandPaletteShortcut(e) {
    for (const shortcut of COMMAND_SHORTCUTS) {
        const keyMatch = e.key.toUpperCase() === shortcut.key ||
                        e.key === shortcut.key

        const ctrlRequired = shortcut.modifiers.includes('ctrl')
        const shiftRequired = shortcut.modifiers.includes('shift')
        const metaRequired = shortcut.modifiers.includes('meta')

        const ctrlMatch = ctrlRequired ? e.ctrlKey : !e.ctrlKey
        const shiftMatch = shiftRequired ? e.shiftKey : !e.shiftKey
        const metaMatch = metaRequired ? e.metaKey : !e.metaKey

        // F1 Sonderfall: keine Modifier nötig
        if (shortcut.key === 'F1' && e.key === 'F1') {
            return true
        }

        if (keyMatch && ctrlMatch && shiftMatch && metaMatch) {
            return true
        }
    }

    return false
}

/**
 * Cmd+D / Ctrl+D im Memory- bzw. Personal-Dict-Pane: aktuellen Absatz loeschen.
 * Strukturell vereinfacht ggue dem Hauptpfad — keine Sections, kein Section-
 * Lock, keine Subtree-Logik. Ohne diesen Handler schlaegt Cmd+D zum Browser
 * durch (Bookmark-Dialog).
 *
 * @param {KeyboardEvent} e
 * @param {object} proseEditor - Memory- oder Personal-Dict-ProseEditor
 * @returns {boolean} true wenn behandelt
 */
function _handleOverlayCmdD(e, proseEditor) {
    if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return false
    if (e.key !== 'd' && e.key !== 'D') return false
    if (!proseEditor) return false
    e.preventDefault()
    const model = proseEditor.model
    if (!model) return true
    if (model.hasSelection()) return true   // analog Hauptpfad: Cmd+D bei Selection ignoriert
    if (!model.cursor) return true
    model.deleteParagraph(model.cursor.paragraphId)
    if (proseEditor.view && typeof proseEditor.view.ensureCursorVisible === 'function') {
        proseEditor.view.ensureCursorVisible(2)
    }
    ctx.layout.isDirty = true
    return true
}

/**
 * Behandelt Zoom-Shortcuts.
 *
 * @param {KeyboardEvent} e
 * @returns {boolean} Event wurde behandelt?
 */
function handleZoomShortcut(e) {
    const isCmdOrCtrl = e.metaKey || e.ctrlKey

    if (!isCmdOrCtrl) return false

    let handled = false

    switch (e.key) {
        case '+':
        case '=':
            handleZoom('in')
            handled = true
            break

        case '-':
            handleZoom('out')
            handled = true
            break

        case '0':
            handleZoom('reset')
            handled = true
            break
    }

    if (handled) {
        e.preventDefault()
    }

    return handled
}

/**
 * Behandelt Zoom-Änderungen.
 *
 * @param {'in' | 'out' | 'reset'} direction
 */
export function handleZoom(direction) {
    let dims

    switch (direction) {
        case 'in':
            dims = ctx.renderer.zoomIn()
            break
        case 'out':
            dims = ctx.renderer.zoomOut()
            break
        case 'reset':
            dims = ctx.renderer.zoomReset()
            break
    }

    handleDimensionChange(dims)
    saveSettings()
}

/**
 * Schaltet Fullscreen um.
 */
export function toggleFullscreen() {
    if (document.fullscreenElement) {
        document.exitFullscreen()
    } else {
        document.documentElement.requestFullscreen()
    }
}

/**
 * Oeffnet das Spellcheck-Menue an der aktuellen Cursor-Position, wenn dort
 * ein Warning liegt. Sonst still nichts (Spec §7.3.4 — leise = nicht-nervig).
 *
 * Phase 3: text/note/info werden gleich behandelt; das Feld kommt aus dem
 * EditorModel-Cursor, der zugehoerige Pane aus dem Layout.
 */
function _openSpellCheckMenuAtCursor() {
    if (!ctx.spellCheckMenu || !ctx.spellCheckManager || !ctx.editor) return

    const model = ctx.editor.model
    const cursor = model.cursor
    if (!cursor) return
    const field = cursor.field || 'text'
    if (field !== 'text' && field !== 'note' && field !== 'info') return
    if (model.isSeparator(cursor.paragraphId)) return

    const para = model.getParagraph(cursor.paragraphId)
    const fieldText = para
        ? (field === 'text' ? (para.text || '') : (para[field] || ''))
        : ''

    // Erst direkt am Cursor pruefen — wenn der Cursor mitten im Wort steht
    // greift das. Wenn nicht: das Wort am Cursor ueber wordRangeAt suchen
    // (Browser-Convention: Whitespace direkt nach dem Wort meint das Wort).
    // Das deckt den haeufigen Fall ab, dass der Cursor unmittelbar nach
    // einem getippten Tippfehler steht.
    let warning = ctx.spellCheckManager.warningAt(cursor.paragraphId, cursor.offset, field)
    if (!warning) {
        const word = wordRangeAt(fieldText, cursor.offset)
        if (word) {
            warning = ctx.spellCheckManager.warningAt(cursor.paragraphId, word.start, field)
        }
    }
    if (!warning) return

    // Anchor-Position des Menues: Cursor-Screen-Pos, klemmt sich selbst an
    // den Bildschirmraendern. Pane-relative {x,y} aus der View, dann Pane-
    // Offset draufaddiert. Der Cursor lebt — je nach Feld — auf MAIN, OUTLINE
    // oder CONTEXT; das Layout liefert die jeweilige Bound.
    const view = ctx.editor.view
    const paneId = field === 'text' ? PaneId.MAIN
        : field === 'note' ? PaneId.OUTLINE
        : PaneId.CONTEXT
    const targetPane = ctx.layout.panes[paneId] || ctx.layout.panes[PaneId.MAIN]
    const screenPos = view ? view.getCursorPosition() : null
    const baseX = targetPane && targetPane.innerBounds ? targetPane.innerBounds.x : 0
    const baseY = targetPane && targetPane.innerBounds ? targetPane.innerBounds.y : 0
    const anchorX = baseX + (screenPos ? screenPos.x : 0)
    // Eine Zeile UNTER den Cursor anchorn — sonst ueberlappt das Menue
    // exakt das beanstandete Wort.
    const anchorY = baseY + (screenPos ? screenPos.y + 1 : 0)

    ctx.spellCheckMenu.show(
        { warning, paragraphId: cursor.paragraphId, field },
        anchorX, anchorY,
        ctx.screen.cols, ctx.screen.rows
    )
    ctx.layout.borderDirty = true
    ctx.layout.isDirty = true
}

/**
 * Behandelt Fullscreen-Wechsel.
 */
export function handleFullscreenChange() {
    // Kurze Verzögerung für korrektes Resize
    setTimeout(() => {
        handleResize()
    }, 100)
}
